import { ModaSofa } from '@dora/backpack';
import * as EC from '@fixtuur/error-context';
import axios from 'axios';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import { formatValidationErrors } from 'io-ts-reporters';
import * as Hooks from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { AppError } from '../../data/appError';
import { CatalogueEntry } from '../../data/catalogueEntry';
import { LayoutFurnitureT } from '../../data/layout/furniture';
import { LayoutModularFurnitureT } from '../../data/layout/modularFurniture';
import * as Pose from '../../data/pose';
import * as Pr from '../../data/product';
import { Product } from '../../data/product';
import * as Room from '../../data/room';
import { Layout, LayoutT } from '../../data/shareLayout';
import * as RoomFixtures from '../../fixtures/room';
import * as Logging from '../../store/actions/logging';
import * as SceneActions from '../../store/actions/scene';
import * as ConfigurablePropertiesStore from '../../store/configurableProperties';
import * as CoreStore from '../../store/core';
import * as LoadingStore from '../../store/loading';
import * as Selectors from '../../store/selectors';
import * as Arya from '../arya';
import { useSelector } from './redux';
import { useAppControl } from './useAppControl';
import { useLaunchData } from './useInitialLayout/useLaunchData';

function findCatalogueEntryForProduct(
    productId: string,
    entries: Array<CatalogueEntry>,
): CatalogueEntry | undefined {
    return entries.find(
        e =>
            e.furnitureDescription.variants.length === 1 &&
            e.furnitureDescription.variants[0].canonical_id === productId,
    );
}

function findCatalogueEntryFor(
    furniture: LayoutFurnitureT,
    entries: Array<CatalogueEntry>,
): CatalogueEntry | undefined {
    return entries.find(
        e =>
            furniture.configurationTemplateId === e.furnitureDescription.configurationTemplateId &&
            furniture.products.length === e.furnitureDescription.variants.length &&
            furniture.products.every(({ productId }) =>
                e.furnitureDescription.variants.map(v => v.canonical_id).includes(productId),
            ),
    );
}

function fetchLayout(
    layoutUrl: string,
    layoutId: string,
    tenant: string,
): TE.TaskEither<EC.ErrorContext<string>, LayoutT> {
    return pipe(
        TE.Do,
        TE.bind('response', () =>
            TE.tryCatch(
                () =>
                    axios.get(`${layoutUrl}/api/v1/${tenant}/layouts/${layoutId}`, {
                        // 40s
                        timeout: 40 * 1000,
                    }),
                (reason: unknown) => EC.fromUnknown<string>(reason),
            ),
        ),
        TE.bind('layout', ({ response }) =>
            response.status === 200
                ? TE.right<EC.ErrorContext<string>, unknown>(response.data)
                : TE.left<EC.ErrorContext<string>, unknown>(
                      EC.construct('failed to fetch layout, expected response 200', {
                          responseStatus: response.status.toString(),
                          responseStatusText: response.statusText,
                          response: JSON.stringify(response, null, 2),
                      }),
                  ),
        ),
        TE.bind('decodedLayout', ({ layout }) =>
            pipe(
                TE.fromEither(Layout.decode(layout)),
                TE.mapLeft(formatValidationErrors),
                TE.mapLeft(errors =>
                    EC.construct('failed to decode layout', {
                        layout: JSON.stringify(layout, null, 2),
                        errors: JSON.stringify(errors, null, 2),
                    }),
                ),
            ),
        ),
        TE.map(({ decodedLayout }) => decodedLayout),
    ) as TE.TaskEither<EC.ErrorContext<string>, LayoutT>;
}

const useDiscontinuedAlert = (
    discontinued: LayoutFurnitureT[] | null,
    alert: (msg: string) => void,
) => {
    const dispatch = useDispatch();
    Hooks.useEffect(() => {
        if (discontinued !== null && discontinued.length > 0) {
            alert('One or more discontinued products found in your layout. They have been removed');
            dispatch(
                SceneActions.loadLayoutWithDiscontinuedProductAction({
                    discontinuedProductIds: discontinued.flatMap(lf =>
                        lf.products.map(p => p.productId),
                    ),
                }),
            );
        }
    }, [discontinued]);
};

/** At the moment an initial layout can either come from a product set (if startSpace is initiated
 * on a Moda product set PDP through the regular button), or from a layout (if startSpace is
 * initiated from loading a layout, e.g. through a save/share link). useLaunchData will set the
 * relevant data to load depending on which case we are in.
 *
 * Eventually all product set layouts should be pushed to the layouts service, but this was not
 * done yet due to time constraints.
 */
export const useInitialLayout = async (
    arya: O.Option<Arya.Interface>,
    /** Called when a new initial layout is applied */
    onNewLayout: () => void = () => {},
    alert: (msg: string) => void,
) => {
    const {
        furniture: furnitureToAdd,
        sofas: sofasToAdd,
        discontinued,
    } = useSelector(Selectors.Loading.currentlyLeftToLoad);

    useDiscontinuedAlert(discontinued, alert);

    const productIdLeftToLoad = useSelector(Selectors.Loading.singleProductIdToLoad);
    const appControl = useAppControl();

    const layoutUrl = useSelector(Selectors.Core.layoutUrl);
    const appRange = useSelector(Selectors.Core.range);
    const currentLoadingId = useSelector(Selectors.Loading.currentLoadingId);
    const appOpen = useSelector(Selectors.Core.open);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);

    const [layoutOrProductToLoad, setLayoutOrProductToLoad] = Hooks.useState<
        | undefined
        | { loadingId: string; layout: LayoutT; kind: 'layout' }
        | { loadingId: string; product: Product; kind: 'product' }
    >(undefined);
    const { selectedProduct, selectedProductSet, selectedProductSetLayout, layoutId, clearData } =
        useLaunchData();

    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        appControl.enhanceCloseApp('clear_layout', () => {
            setLayoutOrProductToLoad(undefined);
            clearData();
        });
    }, [appControl]);

    // Attempts to load layout and sets it in the state hook
    Hooks.useEffect(() => {
        if (selectedProduct) {
            /** Branch 1: launchdata is a product sku (individual product, not a product set) */
            setLayoutOrProductToLoad({
                loadingId: Pr.productId(selectedProduct),
                product: selectedProduct,
                kind: 'product',
            });
        } else if (selectedProductSet && selectedProductSetLayout) {
            /** Branch 2: launchdata is a product set sku */
            if (selectedProductSet.range.length > 1) {
                dispatch(
                    Logging.logWarning(
                        'WARNING: product sets are expected to have only one range, set found with multiple ranges',
                        { productSet: Pr.productId(selectedProductSet) },
                    ),
                );
            }

            const head = selectedProductSet.range[0];
            if (!head) {
                // In this case we cannot continue, so return undefined
                dispatch(
                    Logging.logWarning(
                        'WARNING: product sets are expected to have a range, set found with no range',
                        { productSet: Pr.productId(selectedProductSet) },
                    ),
                );
                return;
            }

            const layout: LayoutT = {
                id: Pr.productId(selectedProductSet),
                furniture: selectedProductSetLayout.furniture,
                modularFurniture: selectedProductSetLayout.modularFurniture,
                rangeFilter: [head],
                space: { room: Room.serialise(RoomFixtures.basic) },
                globalConfigurablePropertySelections:
                    selectedProductSetLayout.globalConfigurablePropertySelections,
            };

            dispatch(Logging.logDebug('layout found', { layout }));
            setLayoutOrProductToLoad({ loadingId: layout.id, layout, kind: 'layout' });
        } else if (layoutId && tenantIdentifier) {
            /** Branch 3: launch data is a layout */
            const fetchAndSet = async () => {
                if (layoutUrl !== undefined) {
                    pipe(
                        fetchLayout(layoutUrl, layoutId, tenantIdentifier),
                        TE.mapLeft(
                            EC.extendContext({
                                requestedLayoutId: layoutId,
                            }),
                        ),
                        TE.mapLeft(ec => {
                            dispatch(CoreStore.error(AppError.LAYOUT_LOAD_FAILED));
                            dispatch(Logging.logError(ec.message, ec.context));
                        }),
                        TE.map(layout =>
                            setLayoutOrProductToLoad({
                                loadingId: layout.id,
                                layout,
                                kind: 'layout',
                            }),
                        ),
                    )();
                }
            };
            fetchAndSet();
        }
    }, [
        layoutUrl,
        selectedProduct,
        selectedProductSet,
        selectedProductSetLayout,
        layoutId,
        tenantIdentifier,
    ]);

    // Loads the actual layout
    Hooks.useEffect(() => {
        const a = O.toNullable(arya);
        if (
            appOpen &&
            a &&
            layoutOrProductToLoad &&
            (currentLoadingId === null || currentLoadingId !== layoutOrProductToLoad.loadingId)
        ) {
            a.clearScene();

            onNewLayout();

            if (layoutOrProductToLoad.kind === 'layout') {
                if (layoutOrProductToLoad?.layout.space !== undefined) {
                    a.updateRoomDimensions(
                        Room.deserialise(layoutOrProductToLoad.layout.space.room),
                    );
                } else {
                    a.updateRoomDimensions(RoomFixtures.basic);
                }
                dispatch(LoadingStore.startLoadingLayout(layoutOrProductToLoad.layout));
                dispatch(CoreStore.setRange(layoutOrProductToLoad.layout.rangeFilter[0]));
                if (layoutOrProductToLoad.layout.globalConfigurablePropertySelections) {
                    dispatch(
                        ConfigurablePropertiesStore.setPropertiesFromLayout(
                            layoutOrProductToLoad.layout.globalConfigurablePropertySelections,
                        ),
                    );

                    layoutOrProductToLoad.layout.globalConfigurablePropertySelections.forEach(
                        selection =>
                            dispatch(
                                ConfigurablePropertiesStore.setConfigurablePropertySelection(
                                    selection,
                                ),
                            ),
                    );
                } else {
                    dispatch(ConfigurablePropertiesStore.setPropertiesFromLayout([]));
                }

                setLayoutOrProductToLoad(undefined);
            } else if (layoutOrProductToLoad.kind === 'product') {
                a.updateRoomDimensions(RoomFixtures.basic);
                // TODO: don't hardcode the range. This needs some bigger picture thinking about
                // what the range concept means outside of Moda
                dispatch(CoreStore.setRange('demo'));
                dispatch(
                    LoadingStore.startLoadingSingleProduct(
                        Pr.productId(layoutOrProductToLoad.product),
                    ),
                );
                setLayoutOrProductToLoad(undefined);
            }
        }
    }, [currentLoadingId, appOpen, arya, layoutOrProductToLoad]);

    useAddProduct(productIdLeftToLoad, arya);
    useAddFurniture(furnitureToAdd, arya);
    useAddModularSofas(sofasToAdd, arya, appRange);
};

function useAddModularSofas(
    sofasToAdd: LayoutModularFurnitureT[],
    arya: O.Option<Arya.Interface>,
    range: string | undefined,
) {
    const dispatch = useDispatch();
    const configurator = useSelector(Selectors.ModaSofa.currentConfigurator);
    const currentGlobalConfigurablePropertySelections = useSelector(
        Selectors.ConfigurableProperties.currentPropertySelections,
    );
    const propertiesFromLayout = useSelector(Selectors.ConfigurableProperties.propertiesFromLayout);

    Hooks.useEffect(() => {
        // Ensure current global properties have been set in state according to layout for those properties that exist in the layout
        const globalPropertiesSatisfied =
            propertiesFromLayout?.length === 0 ||
            (currentGlobalConfigurablePropertySelections?.length > 0 &&
                currentGlobalConfigurablePropertySelections.every(
                    prop =>
                        propertiesFromLayout?.find(cp => cp.propertyKey === prop.propertyKey) ===
                            undefined ||
                        propertiesFromLayout?.find(cp => cp.propertyKey === prop.propertyKey)
                            ?.finish.name === prop.finish.name,
                ));

        if (configurator !== null && sofasToAdd.length > 0 && globalPropertiesSatisfied) {
            sofasToAdd.forEach(sofa => {
                // TODO: ignore sofas with discontinued pieces
                const configuration = configurator(
                    {
                        shape: sofa.shape,
                        left: sofa.size.left,
                        middle: sofa.size.middle,
                        right: sofa.size.right,
                    },
                    sofa.interstitialPlacements ?? {},
                    sofa.cornerStyle ?? ModaSofa.DefaultCornerStyle,
                );

                pipe(
                    arya,
                    O.map(async a => {
                        dispatch(LoadingStore.addingSofa(sofa));
                        try {
                            await a.addModularSofa(
                                configuration,
                                Pose.constructFromRaw(sofa.pose.position, sofa.pose.orientation),
                                range ?? '',
                                'load_layout',
                            );
                        } catch (error) {
                            // This is not always an error case, it may happen if the sofa add is
                            // attempted before the catalogue has loaded yet - and will auto retry
                            // upon the catalogue loading
                            dispatch(Logging.logDebug(`modular sofa failed to add: ${error}`));
                            dispatch(LoadingStore.addingSofaFailed(sofa));
                        }
                    }),
                );
            });
        }
    }, [
        configurator,
        sofasToAdd,
        arya,
        currentGlobalConfigurablePropertySelections,
        propertiesFromLayout,
    ]);
}

function useAddProduct(productIdToAdd: string | null, arya: O.Option<Arya.Interface>) {
    const dispatch = useDispatch();
    const catalogueEntries = useSelector(Selectors.Catalogue.catalogueEntriesForCurrentRange);

    Hooks.useEffect(() => {
        productIdToAdd &&
            pipe(
                // Currently using the catalogue entry as a proxy for a furniture space in order to find
                // a furnitureDescription and summary of the furniture.
                //
                // Eventually the intent is to generate the FurnitureDescription using only the
                // information in the layout, deriving it from the furniture space. We currently do not
                // have furniture spaces for the moda products we support, so this isn't possible.
                catalogueEntries,
                E.fold(
                    err => {},
                    entries => {
                        const catalogueEntry = pipe(
                            E.fromNullable(
                                'cannot find a furniture description for the product space was loaded with',
                            )(findCatalogueEntryForProduct(productIdToAdd, entries)),
                            E.toUnion,
                        );

                        if (typeof catalogueEntry === 'string') {
                            // how to correctly handle this failure? throwing?
                            dispatch(
                                Logging.logWarning(catalogueEntry, {
                                    productIdToAdd,
                                }),
                            );
                            dispatch(CoreStore.error(AppError.LAYOUT_LOAD_FAILED));

                            return;
                        }

                        pipe(
                            arya,
                            O.map(a => {
                                a.addFurniture(
                                    catalogueEntry.furnitureDescription,
                                    catalogueEntry.summary,
                                    'load_layout',
                                    Pose.identity(),
                                );
                            }),
                        );
                    },
                ),
            );
    }, [arya, productIdToAdd, catalogueEntries]);
}

function useAddFurniture(
    /** Can be null if not loaded yet */
    furnitureToAdd: LayoutFurnitureT[] | null,
    arya: O.Option<Arya.Interface>,
) {
    const dispatch = useDispatch();
    const catalogueEntries = useSelector(
        Selectors.Catalogue.catalogueEntriesForCurrentRangeWithoutModularSofaPieces,
    );
    const simpleFurnitureBySku = useSelector(Selectors.Catalogue.simpleFurnitureDescriptionBySku);

    Hooks.useEffect(() => {
        pipe(
            // Currently using the catalogue entry as a proxy for a furniture space in order to find
            // a furnitureDescription and summary of the furniture.
            //
            // Eventually the intent is to generate the FurnitureDescription using only the
            // information in the layout, deriving it from the furniture space. We currently do not
            // have furniture spaces for the moda products we support, so this isn't possible.
            catalogueEntries,
            E.fold(
                err => {
                    // There is no purpose logging the following unless we know we need to debug the
                    // catalogue, since the errors are expected as a normal part of loading
                    // switch (err) {
                    //     case CatalogueError.CATALOGUE_NOT_INITIALISED:
                    //     case CatalogueError.CONFIGURABLE_PROPERTIES_NOT_INITIALISED:
                    //     case CatalogueError.PRODUCTS_NOT_INITIALISED:
                    //     case CatalogueError.RANGE_NOT_INITIALISED:
                    //         dispatch(Logging.logWarning(err));
                    //         break;
                    //     default:
                    //         absurd(err); // ensures all the errors are handled in the switch statement at the type level
                    // }
                },
                entries => {
                    const furnitureDescriptionErrorText =
                        'cannot find furniture description for a furniture in the given layout';

                    furnitureToAdd?.forEach(furniture => {
                        const catalogueEntry = pipe(
                            E.fromNullable(furnitureDescriptionErrorText)(
                                findCatalogueEntryFor(furniture, entries),
                            ),
                            E.toUnion,
                        );

                        if (typeof catalogueEntry === 'string') {
                            // how to correctly handle this failure? throwing?
                            dispatch(
                                Logging.logWarning(catalogueEntry, {
                                    unresolvableFurnitureDetails: {
                                        configurationTemplateId: furniture.configurationTemplateId,
                                        furnitureProducts: furniture.products,
                                    },
                                }),
                            );
                            dispatch(CoreStore.error(AppError.LAYOUT_LOAD_FAILED));

                            return;
                        }

                        pipe(
                            arya,
                            O.map(async a => {
                                // we now know we have a real arya instance, and so this furniture should be
                                // adding to the scene soon. If it becomes an issue, we can try to 're-add' if
                                // the furniture fails, or otherwise expose an error interface
                                dispatch(LoadingStore.addingFurniture(furniture));
                                const furnitureId = await a.addFurniture(
                                    catalogueEntry.furnitureDescription,
                                    catalogueEntry.summary,
                                    'load_layout',
                                    Pose.constructFromRaw(
                                        furniture.pose.position,
                                        furniture.pose.orientation,
                                    ),
                                );
                            }),
                        );
                    });
                },
            ),
        );
    }, [arya, simpleFurnitureBySku, furnitureToAdd, catalogueEntries]);
}
